// Iter-195r — Mini-calendar widget showing what auto-invoice will fire on
// each upcoming day. CEO can expand a day to see the mission list and
// click "Send now" to anticipate a specific (mission, period) tuple.
//
// Loads from GET /api/billing/auto-invoice/forecast?days=14. Each day
// surfaces: count of missions ready that day + a chip per send_mode for
// quick visual scanning.
//
// Iter-196 — Extracted from AutoInvoiceDashboard so we can also render it
// at the top of the Invoices page. Behaviour is unchanged.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Calendar, ChevronDown, ChevronUp, Loader2, Zap, PauseCircle, ExternalLink } from "lucide-react";
import UpcomingInvoiceDetail from "@/components/UpcomingInvoiceDetail";

export default function AutoInvoiceForecastWidget({ onForceRan }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // date → bool
  const [forcing, setForcing] = useState(null); // `${mid}|${y}|${m}` while running
  const [detailSlot, setDetailSlot] = useState(null); // Iter-206d — mission row clicked

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/billing/auto-invoice/forecast", { params: { days: 14 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement prévisions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const forceSend = async (mid, y, mo) => {
    const key = `${mid}|${y}|${mo}`;
    if (!window.confirm(`Forcer l'envoi de la facture ${y}-${String(mo).padStart(2, "0")} pour cette mission maintenant ?\n\nL'override "force" sera enregistré dans le log d'audit.`)) return;
    setForcing(key);
    try {
      const r = await api.post("/billing/auto-invoice/run", {
        mission_id: mid, year: y, month: mo, force: true,
      });
      const status = r.data?.results?.[0]?.status || r.data?.status || "OK";
      toast.success(`Envoi forcé déclenché — statut: ${status}`);
      await load();
      onForceRan && await onForceRan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec envoi forcé");
    } finally { setForcing(null); }
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-5 text-sm text-zinc-500" data-testid="forecast-widget">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement des prévisions…
      </div>
    );
  }
  if (!data) return null;

  if (data.globally_disabled) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="forecast-disabled">
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Auto-invoice globalement désactivé</div>
          <div className="text-xs">Activez "Invoice auto-send" dans Cabinet → Auto-Invoice pour voir les prévisions des prochains jours.</div>
        </div>
      </div>
    );
  }

  const today = data.today;
  const items = data.items || [];
  const nonEmpty = items.filter((it) => it.missions.length > 0);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="forecast-widget">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="font-heading font-bold text-lg text-zinc-900 flex items-center gap-2">
            <Calendar size={16} /> Prévisions d'envoi (14 jours)
          </h2>
          <div className="text-xs text-zinc-500 max-w-2xl">
            Aperçu des factures auto qui partiront chaque jour. Une mission n'apparaît que si tous ses CRA sont approuvés et aucune facture du mois n'existe encore.
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Total prêt</div>
          <div className="text-2xl font-mono font-bold text-zinc-900" data-testid="forecast-ready-total">{data.ready_total}</div>
        </div>
      </div>

      {nonEmpty.length === 0 ? (
        <div className="text-sm text-zinc-500 italic border border-dashed border-zinc-200 rounded p-6 text-center" data-testid="forecast-empty">
          Aucune mission prête à être facturée dans les 14 prochains jours.
        </div>
      ) : (
        <>
          {/* Horizontal strip */}
          <div className="overflow-x-auto -mx-2 px-2 pb-2 mb-3" data-testid="forecast-strip">
            <div className="flex gap-2 min-w-max">
              {items.map((it) => {
                const dt = new Date(it.date + "T00:00:00");
                const isToday = it.date === today;
                const count = it.missions.length;
                const immCount = it.missions.filter((m) => m.send_mode === "IMMEDIATE").length;
                const eomCount = it.missions.filter((m) => m.send_mode === "END_OF_MONTH").length;
                return (
                  <button
                    key={it.date}
                    onClick={() => setExpanded((s) => ({ ...s, [it.date]: !s[it.date] }))}
                    data-testid={`forecast-day-${it.date}`}
                    className={`relative w-[110px] shrink-0 rounded-lg border px-3 py-2 text-left transition-colors ${
                      isToday ? "bg-zinc-950 text-white border-zinc-950"
                      : count > 0 ? "bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-950"
                      : "bg-zinc-50 border-zinc-200 hover:bg-white text-zinc-500"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-mono opacity-70">
                      {dt.toLocaleDateString("fr-FR", { weekday: "short" })}
                    </div>
                    <div className="text-lg font-bold leading-tight">
                      {dt.getDate()} {dt.toLocaleDateString("fr-FR", { month: "short" })}
                    </div>
                    <div className="text-[11px] font-mono mt-1">
                      {count === 0 ? "—" : `${count} facture${count > 1 ? "s" : ""}`}
                    </div>
                    {count > 0 && (
                      <div className="flex gap-1 mt-1 text-[9px] font-mono">
                        {immCount > 0 && <span className="px-1 py-px rounded bg-blue-200 text-blue-900">⚡{immCount}</span>}
                        {eomCount > 0 && <span className="px-1 py-px rounded bg-zinc-200 text-zinc-900">EOM·{eomCount}</span>}
                      </div>
                    )}
                    {isToday && (
                      <span className="absolute top-1 right-1 text-[9px] font-mono px-1 rounded bg-white/20">Aujourd'hui</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Expanded day details */}
          {nonEmpty.filter((it) => expanded[it.date]).map((it) => (
            <div key={`exp-${it.date}`} className="border border-zinc-200 rounded-md mb-2 overflow-hidden" data-testid={`forecast-detail-${it.date}`}>
              <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-zinc-900">
                  {new Date(it.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                  <span className="ml-2 text-xs font-mono font-normal text-zinc-500">
                    {it.missions.length} facture{it.missions.length > 1 ? "s" : ""}
                  </span>
                </div>
                <button
                  onClick={() => setExpanded((s) => ({ ...s, [it.date]: false }))}
                  className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
                >
                  <ChevronUp size={12} /> Replier
                </button>
              </div>
              <ul className="divide-y divide-zinc-100">
                {it.missions.map((m) => {
                  const k = `${m.mission_id}|${m.year}|${m.month}`;
                  const ovr = m.override;
                  const isPaused = ovr?.state === "PAUSED";
                  return (
                    <li key={k} className="px-3 py-2 flex items-center justify-between gap-3 text-sm hover:bg-zinc-50">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left group"
                        onClick={() => setDetailSlot({ ...m, client_name: m.client_name })}
                        data-testid={`forecast-row-${m.mission_id}-${m.year}-${m.month}`}
                        title="Cliquer pour voir le détail de la facture et de l'email"
                      >
                        <div className="font-medium text-zinc-900 truncate group-hover:underline flex items-center gap-1.5">
                          {m.mission_title}
                          <ExternalLink size={11} className="text-zinc-400 group-hover:text-zinc-600 shrink-0" />
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500">
                          {m.client_name} · {m.year}-{String(m.month).padStart(2, "0")} · {m.reason}
                        </div>
                      </button>
                      {isPaused && (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center gap-1 shrink-0" data-testid={`forecast-paused-${m.mission_id}`}>
                          <PauseCircle size={9} /> Pause
                        </span>
                      )}
                      {m.send_mode === "IMMEDIATE" ? (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-900 border border-blue-200">⚡ Immediate</span>
                      ) : (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200">End of month</span>
                      )}
                      {it.date !== today && !isPaused && (
                        <button
                          onClick={(e) => { e.stopPropagation(); forceSend(m.mission_id, m.year, m.month); }}
                          disabled={forcing === k}
                          data-testid={`forecast-force-${m.mission_id}-${m.year}-${m.month}`}
                          title="Envoyer la facture maintenant (anticipation manuelle)"
                          className="inline-flex items-center gap-1 px-2 h-7 text-[11px] bg-zinc-950 text-white rounded hover:bg-zinc-800 disabled:opacity-50 shrink-0"
                        >
                          {forcing === k ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                          Envoyer
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {/* Hint when no detail is open yet */}
          {Object.values(expanded).every((v) => !v) && (
            <div className="text-xs text-zinc-500 italic flex items-center gap-1">
              <ChevronDown size={11} /> Cliquez sur un jour pour voir les missions concernées et forcer un envoi anticipé.
            </div>
          )}
        </>
      )}
      <UpcomingInvoiceDetail
        open={!!detailSlot}
        slot={detailSlot}
        onClose={() => setDetailSlot(null)}
        onChanged={load}
      />
    </div>
  );
}
